import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, X as XIcon, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api/admin/sustainability-tags';
const slug = (t) => t.replace(/\s+/g, '-').toLowerCase();
const send = async (method, body, qs = '') => {
  const res = await fetch(`${API}${qs}`, {
    method, credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Request failed');
  return d;
};

const TagChip = ({ tag, selected, onToggle, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(tag);
  if (editing) {
    const commit = async () => { if (val.trim() && val.trim() !== tag) await onRename(tag, val.trim()); setEditing(false); };
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-brand-navy/40 bg-white pl-2 pr-1 py-0.5">
        <input autoFocus value={val} onChange={e => setVal(e.target.value)} data-testid={`focus-tag-rename-input-${slug(tag)}`}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') setEditing(false); }}
          className="text-xs outline-none w-32 bg-transparent" />
        <button type="button" onClick={commit} className="p-0.5 text-emerald-600" data-testid={`focus-tag-rename-save-${slug(tag)}`}><Check className="h-3 w-3" /></button>
        <button type="button" onClick={() => { setVal(tag); setEditing(false); }} className="p-0.5 text-slate-400"><XIcon className="h-3 w-3" /></button>
      </span>
    );
  }
  return (
    <span className={`group inline-flex items-center rounded-full text-xs font-medium transition-colors ${selected ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
      <button type="button" onClick={onToggle} data-testid={`focus-tag-${slug(tag)}`} className="pl-2.5 pr-1 py-1">{tag}</button>
      <button type="button" onClick={() => setEditing(true)} title="Rename tag" data-testid={`focus-tag-rename-${slug(tag)}`}
        className="p-0.5 opacity-40 group-hover:opacity-100 transition-opacity"><Pencil className="h-3 w-3" /></button>
      <button type="button" onClick={() => onDelete(tag)} title="Delete tag" data-testid={`focus-tag-delete-${slug(tag)}`}
        className="p-0.5 pr-1.5 opacity-40 group-hover:opacity-100 hover:text-red-500 transition-opacity"><XIcon className="h-3 w-3" /></button>
    </span>
  );
};

export default function SustainabilityTagPicker({ value = [], onChange, onTagsChanged }) {
  const [tags, setTags] = useState([]);
  const [usage, setUsage] = useState({});
  const [newTag, setNewTag] = useState('');

  const load = () => send('GET').then(d => { setTags(d.tags || []); setUsage(d.usage || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const all = [...tags, ...value.filter(v => !tags.includes(v))];
  const toggle = (t) => onChange(value.includes(t) ? value.filter(v => v !== t) : [...value, t]);

  const add = async () => {
    const name = newTag.trim();
    if (!name) return;
    try {
      await send('POST', { name });
      setNewTag(''); await load();
      if (!value.includes(name)) onChange([...value, name]);
      toast.success(`Tag "${name}" added`);
    } catch (e) { toast.error(e.message); }
  };
  const rename = async (old, next) => {
    try {
      const d = await send('PUT', { old, new: next });
      setTags(ts => ts.map(t => (t === old ? next : t)));
      onChange(value.map(v => (v === old ? next : v)));
      await load(); onTagsChanged?.();
      toast.success(`Renamed to "${next}" (${d.companies_updated} companies updated)`);
    } catch (e) { toast.error(e.message); }
  };
  const remove = async (t) => {
    const n = usage[t] || 0;
    const msg = n > 0
      ? `Delete "${t}"? It will also be removed from ${n} compan${n === 1 ? 'y' : 'ies'} using it.`
      : `Delete "${t}" from the tag list?`;
    if (!(await window.appConfirm(msg))) return;
    try {
      if (tags.includes(t)) await send('DELETE', null, `?name=${encodeURIComponent(t)}`);
      onChange(value.filter(v => v !== t));
      await load(); onTagsChanged?.();
      toast.success(`Tag "${t}" deleted`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="mt-1.5 p-3 border border-slate-200 rounded-md space-y-3" data-testid="sustainability-focus-selector">
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
        {all.map(t => (
          <TagChip key={t} tag={t} selected={value.includes(t)} onToggle={() => toggle(t)} onRename={rename} onDelete={remove} />
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="New tag, e.g. Hydrogen"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          className="h-8 text-xs" data-testid="focus-tag-new-input" />
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={!newTag.trim()} className="h-8" data-testid="focus-tag-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add tag
        </Button>
      </div>
    </div>
  );
}
